import {NotifyUserHasChangedUseCase} from './notify-user-has-change.use-case';
import {
    SendDeliveryPointSelectionUseCase
} from './../../delivery-point/application/send-delivery-point-selection.use-case';
import {firstValueFrom, from, lastValueFrom, Observable} from 'rxjs';
import {Injectable} from '@angular/core';
import {User} from "@app/core/user/domain/entities/user";
import {UserRemote} from "@app/core/user/domain/user.remote";
import {Encrypt} from "@app/core/user/domain/encrypt";
import {Credentials} from "@app/core/credentials/domain/credentials";
import {UserSaver} from "@app/core/user/domain/user-saver";
import {SaveDeliveryPointUseCase} from "@app/core/delivery-point/application/save-delivery-point.use-case";
import {
    DeliveryPointSelector,
    SelectDeliveryPointUseCase
} from "@app/core/delivery-point/application/select-delivery-point.use-case";
import {PromoterRepo} from '@app/core/promoter/domain/promoter-repo';


export interface LoginUseCaseParams {
    credentials: Credentials;
    deliveryPointSelector: DeliveryPointSelector;
    token: string;
    promoter: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class LoginUseCase {

  constructor(
    private userRemote: UserRemote,
    private userSaver: UserSaver,
    private selectDeliveryPointUseCase: SelectDeliveryPointUseCase,
    private saveDeliveryPointUseCase: SaveDeliveryPointUseCase,
    private sendDeliveryPointSelectionUseCase: SendDeliveryPointSelectionUseCase,
    private notifyUserHasChanged: NotifyUserHasChangedUseCase,
    private promoterRepo: PromoterRepo,
  ) { }

    /**
     * For Europastry, logging in means identifying the user with the credentials
     * and selecting a delivery point is mandatory. If you have only one delivery point,
     * it is automatically selected and the delivery point selector is not shown to the user.
     * @param {LoginUseCaseParams} params - LoginUseCaseParams
     * @returns A promise of a user
     */
  execute(params: LoginUseCaseParams): Observable<User | null> {
      return from(this.login(params));
  }

  /**
   * It logs in a user, selects a delivery point, saves the user and saves the delivery point.
   * @param {LoginUseCaseParams} params - LoginUseCaseParams
   * @returns A promise of a user
   */
  private async login(params: LoginUseCaseParams): Promise<User | null> {
      const hashedPassword = await Encrypt.sha1(params.credentials.password);
      const encryptedCredentials = new Credentials(params.credentials.email, hashedPassword);
      const promoter = params.promoter;
      const user = await firstValueFrom(this.userRemote.login(encryptedCredentials, params.token));
      //Save the credentials temporarily for api calls that require authentication but the user is not logged in.
      await this.userSaver.saveTemporalCredentials(params.credentials);
      const selectedDeliveryPoint = await this.selectDeliveryPointUseCase.execute({
          credentials: encryptedCredentials,
          deliveryPointSelector: params.deliveryPointSelector
      });
      if (selectedDeliveryPoint == null) {
          return null;
      }

      if (promoter != null) {
          this.promoterRepo.save(promoter);
      }

      await this.sendDeliveryPointSelectionUseCase.execute(selectedDeliveryPoint.code, encryptedCredentials);
      await this.userSaver.saveWithPassword(user, params.credentials);
      await lastValueFrom(this.saveDeliveryPointUseCase.execute({deliveryPoint:selectedDeliveryPoint}));
      this.notifyUserHasChanged.execute({email: user.email, deliveryPointCode: selectedDeliveryPoint.code})
      return user;
  }

}
